import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserGroup } from '@models';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteRequest } from '@models/ImageFavorite/imageFavoriteRequest.model';
import { ImageSourceType } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageFavoriteService } from '@services/imageFavorite.service';

// A Scoutview gateway that can supply a dynamic background.
export interface ImageFavoriteGateway {
  serialNumber: string;
  name: string;
}

// Create / edit an image favorite: name, access group and background source
// (an uploaded static image, or the newest capture of a gateway, optionally
// the newest one carrying a given media tag).
// Emits the saved favorite's id once the favorite (and any upload) is saved.
@Component({
  standalone: false,
  selector: 'image-favorite-edit-modal',
  templateUrl: './image-favorite-edit-modal.component.html'
})
export class ImageFavoriteEditModalComponent implements OnInit {
  // initialState
  imageFavorite: ImageFavoriteInfo | null = null;
  existingNames: string[] = [];
  gateways: ImageFavoriteGateway[] = [];
  // Known media tags offered as suggestions; any other tag may be typed in.
  tagSuggestions: string[] = [];
  userGroups: UserGroup[] = [];
  username = 'unknown';

  readonly SourceType = ImageSourceType;

  name = '';
  description = '';
  imageSourceType: number = ImageSourceType.Static;
  gatewaySourceId: string | null = null;
  imageTag: string | null = null;
  userGroupId: number | null = null;

  selectedFile: File | null = null;
  previewSrc: string | null = null;
  saving = false;
  modalDragOn = false;

  @Output() onSaved = new EventEmitter<number>();

  constructor(public bsModalRef: BsModalRef,
              private imageFavoriteService: ImageFavoriteService,
              private toastr: ToastrService) {}

  ngOnInit(): void {
    if (this.imageFavorite != undefined) {
      this.name = this.imageFavorite.name;
      this.description = this.imageFavorite.description ?? '';
      this.imageSourceType = this.imageFavorite.imageSourceType;
      this.gatewaySourceId = this.imageFavorite.gatewaySourceId;
      this.imageTag = this.imageFavorite.imageTag;
      this.userGroupId = this.imageFavorite.userGroupId;
    }
    if (this.userGroupId == undefined) {
      this.userGroupId = this.userGroups.some(g => g.id === -1) ? -1 : null;
    }
  }

  get isUpdate(): boolean {
    return this.imageFavorite != undefined && this.imageFavorite.id > 0;
  }

  get canSave(): boolean {
    if (this.saving || this.name.trim() === '') {
      return false;
    }
    if (this.imageSourceType === ImageSourceType.Dynamic) {
      return this.gatewaySourceId != undefined && this.gatewaySourceId !== '';
    }
    // A new static favorite needs an image; an existing one may keep its current image.
    return this.selectedFile != undefined || (this.isUpdate && this.imageFavorite.blobName != undefined);
  }

  setDragOn(on: boolean): void {
    this.modalDragOn = on;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files != undefined && input.files.length > 0 ? input.files[0] : null;
    this.selectedFile = file;
    this.previewSrc = null;
    if (file != undefined) {
      const reader = new FileReader();
      reader.onload = () => this.previewSrc = reader.result as string;
      reader.readAsDataURL(file);
    }
  }

  save(): void {
    const name = this.name.trim();
    if (this.existingNames.some(n => n.toLowerCase() === name.toLowerCase() && n !== this.imageFavorite?.name)) {
      this.toastr.error('Image favorite name already exists. Please select a new one');
      return;
    }

    const isDynamic = this.imageSourceType === ImageSourceType.Dynamic;
    const request: ImageFavoriteRequest = {
      id: this.isUpdate ? this.imageFavorite.id : 0,
      name,
      description: this.description.trim() !== '' ? this.description.trim() : null,
      imageSourceType: this.imageSourceType,
      gatewaySourceId: isDynamic ? this.gatewaySourceId : null,
      imageTag: (isDynamic && this.imageTag != undefined && this.imageTag.trim() !== '') ? this.imageTag.trim() : null,
      details: this.imageFavorite?.details ?? null,
      userName: this.isUpdate ? this.imageFavorite.userName : this.username,
      userGroupId: (this.userGroupId == undefined || this.userGroupId < 0) ? null : this.userGroupId
    };

    const save$: Observable<number> = this.isUpdate
      ? this.imageFavoriteService.updateImageFavorite(request, this.username).pipe(map(() => request.id))
      : this.imageFavoriteService.createImageFavorite(request).pipe(map((newId: any) => Number(newId)));

    this.saving = true;
    save$.pipe(
      switchMap((id: number) => {
        if (isNaN(id) || id <= 0) {
          throw new Error('Invalid image favorite id');
        }
        return (!isDynamic && this.selectedFile != undefined)
          ? this.imageFavoriteService.uploadImage(id, this.selectedFile, this.username).pipe(map(() => id))
          : of(id);
      })
    ).subscribe(
      (id: number) => {
        this.saving = false;
        this.toastr.success('Image favorite saved');
        this.onSaved.emit(id);
        this.bsModalRef.hide();
      },
      _err => {
        this.saving = false;
        this.toastr.error('Unable to save image favorite');
      });
  }

  cancel(): void {
    this.bsModalRef.hide();
  }
}
